// ─────────────────────────────────────────────────────────────────────────────
// HeaderDebugReadout — TEMPORARY. Delete once the /apply header band is
// explained. See the removal checklist at the bottom of this file.
//
// ⚠️ WHY THIS EXISTS AT ALL: the measurement it replaces cannot be taken. Safari
// Web Inspector is the only way to attach a debugger to an iOS WKWebView and it
// requires a Mac. There isn't one. So the numbers have to be rendered INTO the
// frame the camera is already pointed at — the video becomes the instrument.
//
// ⚠️ IT MUST BE LIVE, NOT SAMPLED. The open question is what changes DURING a
// scroll cycle and does not revert afterwards; a reading taken at mount answers
// nothing. Everything below is re-read every animation frame, and each row also
// carries the MIN…MAX envelope observed since mount — that is the row that
// actually answers the question, because a value that drifted and stayed drifted
// shows an envelope wider than its current value even when the scroll has ended
// and everything looks correct again.
//
// ⚠️ AND IT MUST NOT PERTURB WHAT IT MEASURES. It is position:fixed in the
// BOTTOM-left corner (the header is at the top — they cannot overlap), it is
// pointer-events:none so it can never eat a tap meant for the form, it has its
// own stacking context, and it is layout+paint contained so nothing inside it
// can reach the surrounding layout. It touches no ref, no state and no DOM that
// ApplicationForm owns: the header is found by [data-hdr] and only ever READ.
// Nothing here can fire the header's ResizeObserver, because that observer fires
// on the header's own box changing and this element is not in its subtree.
//
// The frame counter in the title row is not decoration. If it stops advancing in
// the video, the readout froze and every number below it is stale — without it a
// frozen overlay is indistinguishable from a genuinely stable measurement.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef } from 'react';
import { COMMIT_SHA } from '../constants/build';

const FLAG = 'hdrdebug';

// ─────────────────────────────────────────────────────────────────────────────
// WHAT THIS INSTRUMENT ACTUALLY FOUND — 20 Aug 2026, measured on-device, iOS.
// Recorded here because it is now a FACT rather than a theory, and because the
// next person to touch the /apply header will start from the same wrong guess
// everyone here started from.
//
// ACROSS A FULL SCROLL CYCLE:
//   • --safe-top, live env(safe-area-inset-top) and the header's computed
//     paddingTop were CONSTANT. Their MIN…MAX envelopes never widened — the only
//     reading that could have shown it, since a value that drifted and came back
//     is indistinguishable from one that never moved if you sample it once.
//   • visualViewport.height / window.innerHeight SWING: 797 → 844 — exactly one
//     top safe-area inset. (47px is the iPhone 14/15 non-Pro inset: the NUMBER is
//     device-specific, the behaviour is not.)
//   • visualViewport.offsetTop stayed 0.0 throughout. It is the viewport HEIGHT
//     that changes, never its origin.
//
// ⚠️ STRUCK FROM THE EVIDENCE — [data-hdr] rect.top. It reads 0.0 constantly and
// that proves NOTHING: getBoundingClientRect() is viewport-relative and the header
// is position:fixed;top:0, so 0.0 is what it returns BY DEFINITION, wherever the
// viewport sits on screen. It was originally written up here as though it showed
// stability, and that misreading cost a round — it was taken to mean "the header
// does not move" when the header demonstrably still moves on screen. The row stays
// in the readout below ONLY as a tripwire for the header ceasing to be fixed.
// Never quote it as evidence about on-screen position.
//
// SO, WHAT THIS ACTUALLY ESTABLISHES — a LAYER conclusion, not a CSS one:
// every value above is measured relative to the WEBVIEW. With offsetTop pinned at 0
// and rect.top pinned at 0 by construction, the page cannot observe its own
// container moving; the only signal that leaks through is vv.height. The header is
// stable in every coordinate system CSS can reach AND still moves on screen — which
// places the movement in the native shell sizing of the WebView, outside this file
// and outside CSS.
//
// ⚠️ DO NOT ATTEMPT A FOURTH CSS FIX. Three have failed, necessarily: CSS cannot
// correct a change to its own container frame. Leading hypothesis is the shell
// config — ios.contentInset:'always' makes the WKWebView scroll view apply
// safe-area inset adjustment WHILE the app also pads by env() under
// StatusBar.overlaysWebView:true, and a height alternating by exactly one inset is
// what that double-handling would look like. Queued as a NATIVE build change; the
// config is baked into the binary and is not OTA-deliverable. See
// frontend/capacitor.config.ts.
// ─────────────────────────────────────────────────────────────────────────────

// ⚠️ THE QUERY PARAM IS THE WEB PATH ONLY. Inside the Capacitor WebView there is
// no address bar, so ?hdrdebug=1 is untypeable on the device this is for. Reading
// the param LATCHES it into localStorage, and the latch is what /apply actually
// reads — so it survives navigation, and staff can flip it from /admin/ota on a
// device that has no way to type a URL. ?hdrdebug=0 clears it.
export function isHeaderDebugEnabled() {
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.has(FLAG)) {
      const on = q.get(FLAG) !== '0' && q.get(FLAG) !== 'false';
      window.localStorage.setItem(FLAG, on ? '1' : '0');
      return on;
    }
    return window.localStorage.getItem(FLAG) === '1';
  } catch (_) {
    return false; // private mode / storage disabled — stay off
  }
}

export function setHeaderDebugEnabled(on) {
  try { window.localStorage.setItem(FLAG, on ? '1' : '0'); } catch (_) { /* no-op */ }
}

// label, id. Order follows what the Aug 2026 run established: vv.height is the
// only value that moves, so it and its origin sit together, and the inset rows
// stay because ruling them out again should be cheap.
//
// ⚠️ 'rect.top' IS NOT EVIDENCE OF POSITION. A position:fixed;top:0 element
// returns 0.0 from getBoundingClientRect() by definition. It is retained purely
// as a tripwire for the header ceasing to be fixed — see the findings block above,
// where reading it as stability cost a round.
//
// The '--safe-top' row is gone with the latch that set it (ApplicationForm.js):
// the property no longer exists, and a row that always reads '—' is the kind of
// noise that gets mistaken for a measurement.
const ROWS = [
  ['env() live', 'envLive'],
  ['rect.top', 'top'],
  ['rect.h', 'height'],
  ['padTop', 'padTop'],
  ['scrollY', 'scrollY'],
  ['vv.height', 'vvH'],
  ['vv.offTop', 'vvTop'],
  ['innerH', 'innerH'],
];

export default function HeaderDebugReadout() {
  const nowRefs = useRef({});
  const rangeRefs = useRef({});
  const frameRef = useRef(null);
  const probeRef = useRef(null);

  useEffect(() => {
    const hdr = document.querySelector('[data-hdr]');
    // getComputedStyle returns a LIVE declaration — resolved once, read every
    // frame. Calling it per frame would be the expensive version of this.
    const hdrCs = hdr ? window.getComputedStyle(hdr) : null;
    const probeCs = probeRef.current ? window.getComputedStyle(probeRef.current) : null;

    const ext = Object.create(null);
    let frames = 0;
    let raf = 0;

    const px = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : null; };
    const fmt = (n) => (n == null ? '—' : n.toFixed(1));

    const put = (id, val) => {
      const nowEl = nowRefs.current[id];
      if (nowEl) nowEl.textContent = fmt(val);
      if (val == null) return;
      const e = ext[id] || (ext[id] = { min: val, max: val });
      if (val < e.min) e.min = val;
      if (val > e.max) e.max = val;
      const rangeEl = rangeRefs.current[id];
      // '·' means "never moved". Anything else is a value that changed at some
      // point since mount — which is the finding, whether or not it reverted.
      if (rangeEl) rangeEl.textContent = (e.max - e.min) < 0.05 ? '·' : `${fmt(e.min)}…${fmt(e.max)}`;
    };

    const tick = () => {
      frames += 1;
      if (frameRef.current) frameRef.current.textContent = String(frames);

      const rect = hdr ? hdr.getBoundingClientRect() : null;
      const vv = window.visualViewport;

      // The LIVE env(safe-area-inset-top), from our own probe — now the only inset
      // value there is, since the header reads env() directly again. The Aug 2026
      // run showed it flat across a full scroll cycle, which is what ruled the
      // inset out as the moving part. Kept because re-confirming it after a shell
      // change (contentInset / overlaysWebView) costs one glance.
      put('envLive', probeCs ? px(probeCs.paddingTop) : null);
      put('top', rect ? rect.top : null);
      put('height', rect ? rect.height : null);
      put('padTop', hdrCs ? px(hdrCs.paddingTop) : null);
      put('scrollY', window.scrollY);
      put('vvH', vv ? vv.height : null);
      put('vvTop', vv ? vv.offsetTop : null);
      put('innerH', window.innerHeight);

      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, []);

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        left: 0,
        bottom: 'env(safe-area-inset-bottom)', // clear of the home indicator, or the numbers are unreadable
        zIndex: 2147483000,
        // ⚠️ NON-NEGOTIABLE: never intercepts a tap. This sits over a live
        // borrower form; a debug overlay that swallows a button is a worse bug
        // than the one it is measuring.
        pointerEvents: 'none',
        isolation: 'isolate',   // its own stacking context
        contain: 'layout paint', // nothing inside can reach the page's layout
        background: 'rgba(0,0,0,0.86)',
        color: '#fff',
        font: '600 11px/1.4 ui-monospace, SFMono-Regular, Menlo, monospace',
        fontVariantNumeric: 'tabular-nums', // digits keep their columns; no jitter on video
        padding: '7px 9px',
        borderTopRightRadius: 8,
        maxWidth: '62vw',
      }}
    >
      {/* Live env(safe-area-inset-top) probe. Zero-size and hidden — it exists
          only to be read by getComputedStyle. env() resolves against the
          viewport, not the element, so its position here is irrelevant. */}
      <div
        ref={probeRef}
        style={{ position: 'absolute', width: 0, height: 0, visibility: 'hidden', paddingTop: 'env(safe-area-inset-top)' }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, color: '#7dd3fc', marginBottom: 3 }}>
        {/* Which bundle the video is of — the reason REACT_APP_COMMIT_SHA exists. */}
        <span>hdrdebug {String(COMMIT_SHA).slice(0, 7)}</span>
        <span>f<span ref={frameRef}>0</span></span>
      </div>

      {ROWS.map(([label, id]) => (
        <div key={id} style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
          <span style={{ width: 66, color: '#9ca3af' }}>{label}</span>
          <span ref={(el) => { nowRefs.current[id] = el; }} style={{ width: 46, textAlign: 'right' }}>—</span>
          <span ref={(el) => { rangeRefs.current[id] = el; }} style={{ color: '#fbbf24', textAlign: 'right' }}>·</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REMOVAL CHECKLIST (this is scaffolding, not a feature):
//   1. delete this file
//   2. ApplicationForm.js — the import, the useState gate, the render line
//   3. AdminOta.jsx — the "Header debug readout" toggle block + its import
//   4. leave data-hdr in place; it costs nothing and the next person measuring
//      this header will want it
// ─────────────────────────────────────────────────────────────────────────────
